/**
 * Component: HistoricalAnalytics
 * Purpose: Displays historical business analytics snapshots for a selected
 * date. Fetches list of snapshots, defaults to most recent valid date (>=
 * 2025-08-18), and renders various metric sections.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, RefreshCw } from 'lucide-react';
import adminApi from '../../../api/accounts/adminApi';
import { LoadingSpinner } from '../../ui/feedback/loading-spinner.jsx';
import { formatDateLong, formatCapturedAt } from './analyticsFormatters';
import {
  RevenueMetrics,
  CustomerMetrics,
  TrialMetrics,
  ChurnMetrics,
  EngagementMetrics,
  PlatformMetrics
} from './sections';

const HistoricalAnalytics = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [snapshotData, setSnapshotData] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);

  // Load snapshot data for specific date
  const loadSnapshotData = useCallback(async () => {
    try {
      setError(null);
      
      // Load all available snapshots to get the date list and find the specific snapshot
      const snapshotsResponse = await adminApi.getAnalyticsSnapshots('all', 200);
      const snapshots = snapshotsResponse.snapshots || [];
      
      // Filter snapshots to only include dates after August 18, 2025
      const minDate = new Date('2025-08-18');
      const validSnapshots = snapshots.filter(snapshot => {
        const snapshotDate = new Date(snapshot.date);
        return snapshotDate >= minDate;
      });
      
      // Set available dates
      const dates = validSnapshots.map(s => s.date).sort().reverse();
      setAvailableDates(dates);
      
      // Default to most recent if no date selected or current date unavailable
      const mostRecentDate = dates[0];
      if (dates.length > 0 && (!selectedDate || !dates.includes(selectedDate))) {
        setSelectedDate(mostRecentDate);
        return;
      }
      
      // Find the snapshot for the selected date
      const snapshot = validSnapshots.find(s => s.date === selectedDate);
      
      if (snapshot) {
        setSnapshotData(snapshot);
      } else {
        setSnapshotData(null);
        if (dates.length === 0) {
          setError('No analytics snapshots available yet. Please run a snapshot first.');
        } else {
          setError(`No snapshot found for ${selectedDate}. Available dates start from August 18, 2025.`);
        }
      }
      
    } catch (err) {
      console.error('HistoricalAnalytics - Error loading snapshot:', err);
      setError(err.message || 'Failed to load analytics snapshot');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadSnapshotData();
  }, [loadSnapshotData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadSnapshotData();
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setIsLoading(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 w-full max-w-[1800px] mx-auto">
        <div className="bg-red-50/70 dark:bg-red-900/30 backdrop-blur-md border border-red-200 dark:border-red-800 rounded-lg p-4 sm:p-6">
          <h3 className="text-lg font-medium text-red-800 dark:text-red-400 mb-2">
            Error Loading Historical Analytics
          </h3>
          <p className="text-red-600 dark:text-red-300">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Date Selection Controls */}
      <DateSelectionControls
        selectedDate={selectedDate}
        availableDates={availableDates}
        snapshotData={snapshotData}
        isRefreshing={isRefreshing}
        onDateChange={handleDateChange}
        onRefresh={handleRefresh}
      />

      {/* Display snapshot data if available */}
      {snapshotData && (
        <>
          <RevenueMetrics snapshotData={snapshotData} />
          <CustomerMetrics snapshotData={snapshotData} />
          <TrialMetrics snapshotData={snapshotData} />
          <ChurnMetrics snapshotData={snapshotData} />
          <EngagementMetrics snapshotData={snapshotData} />
          <PlatformMetrics snapshotData={snapshotData} />
        </>
      )}

      {/* No Data Message */}
      {!snapshotData && !isLoading && (
        <NoDataMessage selectedDate={selectedDate} availableDates={availableDates} />
      )}

      {/* Data Source Info */}
      {snapshotData && (
        <DataSourceInfo selectedDate={selectedDate} snapshotData={snapshotData} />
      )}
    </div>
  );
};

// Date Selection Controls Component
function DateSelectionControls({ selectedDate, availableDates, snapshotData, isRefreshing, onDateChange, onRefresh }) {
  return (
    <div className="mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
          <select
            value={selectedDate}
            onChange={(e) => onDateChange(e.target.value)}
            className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-transparent backdrop-blur-md text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm min-w-[200px]"
          >
            {availableDates.length > 0 ? (
              availableDates.map(date => (
                <option key={date} value={date}>
                  {formatDateLong(date)}
                </option>
              ))
            ) : (
              <option value="">No snapshots available</option>
            )}
          </select>
        </div>

        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-transparent backdrop-blur-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/30 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm transition-colors"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {selectedDate && (
        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          <p>Viewing snapshot for <strong>{formatDateLong(selectedDate)}</strong></p>
          {snapshotData && (
            <p className="text-xs mt-1">
              Captured at {formatCapturedAt(snapshotData.capturedAt, snapshotData.timestamp)} • 
              Capture time: {snapshotData.captureMetadata?.captureTimeMs || 0}ms
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// No Data Message Component
function NoDataMessage({ selectedDate, availableDates }) {
  return (
    <div className="text-center py-12">
      <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
        No Snapshot Available
      </h3>
      <p className="text-gray-600 dark:text-gray-400 mb-4">
        {availableDates.length === 0 
          ? "No analytics snapshots have been captured yet."
          : `No snapshot found for ${selectedDate}.`
        }
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Snapshots are available starting from August 18, 2025.
      </p>
    </div>
  );
}

// Data Source Info Component
function DataSourceInfo({ selectedDate, snapshotData }) {
  return (
    <div className="bg-blue-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4 border border-blue-200 dark:border-gray-700">
      <p className="text-sm text-blue-700 dark:text-blue-300">
        <strong>Historical Snapshot:</strong> These are the cumulative business totals as they appeared on {formatDateLong(selectedDate)} at {formatCapturedAt(snapshotData.capturedAt, snapshotData.timestamp)}. 
        This shows what your "All Time" analytics looked like on that specific date. Free users are excluded from all calculations.
      </p>
    </div>
  );
}

export default HistoricalAnalytics;
